import { useCallback, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { orderBy, range } from 'lodash'
import { DateTime } from 'luxon'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import {
  selectNetPayroll,
  selectPayrollByUuid,
  selectPayrollCanBeCanceled,
  selectPayrolls,
} from '../payroll.selectors'
import { getIsFetchingOrNotStarted } from '@/reducers/fetch'
import {
  FETCH_PAYROLL_KEY,
  fetchAllPayrolls,
  putCancelPayroll,
} from '../payrollActions'
import { formatCurrency } from '@/utils/currencyHelpers'
import {
  Text,
  Button,
  Table,
  Popup,
  Link,
  Alert,
  Icon,
  Select,
  Dialog,
  Loader,
} from '@/ui'
import { TypographyStyles } from '@/styles'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS, DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'

// TODO: Migrate to V2
import { valsToDropdownOptions } from '@/base'

const CancelModal = ({
  uuid,
  close,
  open,
}: {
  uuid: string
  close: () => void
  open: boolean
}) => {
  const dispatch = useAppDispatch()
  const [loading, setLoading] = useState(false)

  const cancelPayroll = useCallback(async () => {
    setLoading(true)
    // This is a very slow call (4 sec)
    const res = await putCancelPayroll(uuid)(dispatch)

    setLoading(false)
    if (res) {
      close()
    }
  }, [close, dispatch, uuid])

  return (
    <Dialog open={open} onOpenChange={(open) => !open && close()}>
      <Dialog.Content className="max-w-sm">
        <Dialog.Title>Cancel Payroll</Dialog.Title>

        <div className="flex flex-col gap-4 mt-2">
          <Text>
            <b>You are canceling payroll. Are you sure you want to cancel?</b>
          </Text>

          <Text>
            The information you have entered for this payroll will not be saved.
          </Text>

          {loading ? (
            <div className="flex justify-center py-4">
              <Loader />
            </div>
          ) : (
            <Dialog.Footer>
              <Button onClick={cancelPayroll} variant="danger">
                Cancel Payroll
              </Button>
              <Button variant="secondary" onClick={close}>
                Go Back
              </Button>
            </Dialog.Footer>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  )
}

const PayrollRow = ({ uuid }: { uuid: string }) => {
  const [showModal, setShowModal] = useState(false)
  const payroll = useReselector(selectPayrollByUuid, uuid)
  const showCancel = useReselector(selectPayrollCanBeCanceled, uuid)

  if (!payroll) {
    return null
  }

  const {
    pay_period: { pay_schedule_uuid, start_date, end_date },
    processed_date,
    totals,
  } = payroll

  const companyDebit = totals?.company_debit || '0.00'

  const type = pay_schedule_uuid ? 'Regular' : 'Off-Cycle'

  return (
    <Table.Row>
      <Table.Cell className="pl-2">
        <Text>
          {processed_date
            ? moment(processed_date).format(DATE_FORMATS.DISPLAY_SHORT)
            : ''}
        </Text>
        {showCancel && (
          <Button variant="link" onClick={() => setShowModal(true)}>
            Cancel
          </Button>
        )}
      </Table.Cell>
      <Table.Cell>{type}</Table.Cell>
      <Table.Cell>
        {start_date && end_date
          ? `${moment(start_date).format(
              DATE_FORMATS.DISPLAY_SHORT
            )} - ${moment(end_date).format(DATE_FORMATS.DISPLAY_SHORT)}`
          : ''}
      </Table.Cell>
      <Table.Cell>{formatCurrency(companyDebit)}</Table.Cell>
      <Table.Cell>
        <Link href={`/payroll/history/${uuid}`}>View Details</Link>
      </Table.Cell>
      {showModal && (
        <CancelModal
          uuid={uuid}
          close={() => setShowModal(false)}
          open={showModal}
        />
      )}
    </Table.Row>
  )
}

export const EmployeePayrollHistoryTab = () => {
  const dispatch = useAppDispatch()
  const payrollFetching = useReselector(
    getIsFetchingOrNotStarted,
    FETCH_PAYROLL_KEY
  )
  const [year, setYear] = useState(DateTime.now().year)
  const payrolls = useReselector(selectPayrolls)
  const { netDebit, netPayrollTaxes, netPaidToEmployees } = useReselector(
    selectNetPayroll,
    year
  )

  const yearOptions = valsToDropdownOptions(
    range(2020, DateTime.now().year + 1).reverse()
  )

  useEffect(() => {
    // Gusto has limitation of fetching 3 months ahead
    const endDate = DateTime.min(
      DateTime.fromObject({ year }).endOf('year'),
      DateTime.now().plus({ months: 3 }).minus({ day: 1 })
    )

    dispatch(
      fetchAllPayrolls({
        end_date: endDate.toFormat(DATE_FORMATS_LUXON.GUSTO_SUBMIT),
        start_date: DateTime.fromObject({ year })
          .startOf('year')
          .toFormat(DATE_FORMATS_LUXON.GUSTO_SUBMIT),
        payroll_types: ['off_cycle', 'regular'],
        include: ['totals'],
      })
    )
  }, [dispatch, year])

  const payrollsToShow = useMemo(
    () =>
      orderBy(
        Object.values(payrolls).filter(
          ({ processed_date }) =>
            processed_date && moment(processed_date).year() === year
        ),
        (payroll) => payroll.processed_date,
        ['desc']
      ),
    [payrolls, year]
  )

  return (
    <div className="flex flex-col gap-6">
      <Alert
        variant="default"
        description="This is a list of all the payrolls you’ve made. Toggle between years to
        review your payroll history, view payroll details or cancel a payroll."
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <Text className="w-1/2" variant={TypographyStyles.H2}>
          Payroll Summaries
        </Text>
        <div className="w-1/3 flex justify-end">
          <Select options={yearOptions} value={year} onValueChange={setYear} />
        </div>
      </div>

      {!payrollFetching && (
        <div className="flex flex-col md:flex-row gap-6">
          <div className="w-full md:w-1/3">
            <div className="flex items-center gap-2">
              <Text variant={TypographyStyles.BODY_SM}>
                Total Amount Debited from Bank
              </Text>
              <Popup
                content="This is the amount that will be taken from your attached bank account for this payroll cycle."
                trigger={<Icon icon={faInfoCircle} />}
              />
            </div>
            <Text variant={TypographyStyles.DISPLAY_SM} className="mt-2">
              {formatCurrency(netDebit)}
            </Text>
          </div>
          <div className="w-full sm:w-1/3">
            <div className="flex items-center gap-2">
              <Text variant={TypographyStyles.BODY_SM}>
                Total Amount Paid in Payroll Taxes
              </Text>
              <Popup
                content="This includes federal and state payroll taxes paid in this payroll cycle."
                trigger={<Icon icon={faInfoCircle} />}
              />
            </div>
            <Text variant={TypographyStyles.DISPLAY_SM} className="mt-2">
              {formatCurrency(netPayrollTaxes)}
            </Text>
          </div>
          <div className="w-full sm:w-1/3">
            <div className="flex items-center gap-2">
              <Text variant={TypographyStyles.BODY_SM}>
                Total Amount Paid to Employees
              </Text>
              <Popup
                content="This consists of employee wages, reimbursements, and bonuses paid in this payroll cycle."
                trigger={<Icon icon={faInfoCircle} />}
              />
            </div>
            <Text variant={TypographyStyles.DISPLAY_SM} className="mt-2">
              {formatCurrency(netPaidToEmployees)}
            </Text>
          </div>
        </div>
      )}

      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Date Processed</Table.HeaderCell>
            <Table.HeaderCell>Payroll Type</Table.HeaderCell>
            <Table.HeaderCell>Payroll Period</Table.HeaderCell>
            <Table.HeaderCell>Total Debited</Table.HeaderCell>
            <Table.HeaderCell />
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {payrollsToShow.map(({ payroll_uuid }) => (
            <PayrollRow key={payroll_uuid} uuid={payroll_uuid} />
          ))}
        </Table.Body>
      </Table>
    </div>
  )
}
